import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import useQuery from '../../hooks/useQuery';
import useCurrentBuilderPage from '../../../../admin/hooks/useCurrentBuilderPage';
import useCurrentSite from '../../hooks/useCurrentSite';
import { FLIPPER_VOYAGER_MEDIA_GALLERY } from '../../../../constants/flippers';
import useFlipper from '../../hooks/useFlipper';
import getPlaceholderMetadata from '../../../media/getPlaceholderMetadata';
import Editor from './Editor';

export const GET_MEDIA = gql`
  query GetMedia($ids: [ID!], $filenames: [String!], $locale: String) {
    media: mediaByIds(ids: $ids, filenames: $filenames, excludeTrash: false) {
      id
      ...ImageWidgetEditor
    }
  }

  ${Editor.fragments.media}
`;

const withImages = (Component) => {
  const ImagesWrapper = ({ images, otherImages, ...rest }) => {
    const mediaGalleryEnabled = useFlipper(FLIPPER_VOYAGER_MEDIA_GALLERY);
    const { locale } = useCurrentBuilderPage();
    const { id: companyId } = useCurrentSite();
    const { heroImages } = rest;
    let payloadImages = [];
    if ((images && images.length) || (otherImages && otherImages.length)) {
      payloadImages = [...images, ...otherImages];
    } else if (heroImages && heroImages.length) {
      payloadImages = heroImages;
    }
    const filteredImages = payloadImages.filter(Boolean).map((img) => {
      if (img && img.file) {
        return {
          ...img,
          filename: img.file.replace(`${companyId}/`, ''),
        };
      }
      return img;
    });

    const { ids, filenames } = mediaGalleryEnabled ? filteredImages.reduce((acc, img) => {
      const { ids: accIds, filenames: accFilenames } = acc;
      if (img && img.mediumId) {
        return ({
          ids: [...accIds, img.mediumId],
          filenames: accFilenames,
        });
      }

      if (img && img.file) {
        return ({
          ids: accIds,
          filenames: [...accFilenames, img.filename],
        });
      }

      return acc;
    }, { ids: [], filenames: [] }) : { ids: [], filenames: [] };

    const { data, loading: loadingMedia } = useQuery(
      GET_MEDIA,
      { variables: { ids, filenames, locale } },
    );

    const editorImages = filteredImages.map((img) => {
      if (data && data.media) {
        const { media } = data;
        const loadedMedium = media.find(medium => (
          (medium.id === img.mediumId) || (medium.filename === img.filename)
        ));
        if (loadedMedium) {
          const { altText, title } = getPlaceholderMetadata(loadedMedium, locale);

          return {
            ...img,
            ...loadedMedium,
            mediumId: loadedMedium.id,
            alt: altText,
            title,
          };
        }
      }

      return img;
    });

    return (
      <Component
        editorImages={editorImages}
        loadingMedia={loadingMedia}
        {...rest}
      />
    );
  };

  ImagesWrapper.propTypes = {
    images: PropTypes.arrayOf(PropTypes.shape({
      file: PropTypes.string,
      mediumId: PropTypes.string,
    })),
    otherImages: PropTypes.arrayOf(PropTypes.shape({
      file: PropTypes.string,
      mediumId: PropTypes.string,
    })),
    heroImages: PropTypes.arrayOf(PropTypes.shape({
      file: PropTypes.string,
      mediumId: PropTypes.string,
    })),
  };

  ImagesWrapper.defaultProps = {
    images: [],
    otherImages: [],
    heroImages: [],
  };

  return ImagesWrapper;
};

export default withImages;
